// HU-01, HU-08 y HU-36. El ciclo de vida del borrador contra el servidor.
//
// Vive fuera de la pantalla porque son tres reglas que se enredan entre si y que no
// tienen nada que ver con pintar campos:
//
//   1. El caso NACE en el servidor. Hasta que `POST /casos` responde no hay id, y
//      sin id no hay `PUT` posible. El primer guardado tiene que esperar a eso.
//   2. `version_bloqueo` es del servidor, no del cliente (HU-36). Cada `PUT`
//      devuelve la nueva y hay que quedarse con ELLA; inventarla en el cliente
//      —incrementarla a mano, por ejemplo— rompe el bloqueo optimista en silencio.
//   3. El autoguardado no puede solaparse consigo mismo. Dos `PUT` en vuelo con la
//      misma `version_esperada` hacen que el segundo reciba 409 contra su propio
//      hermano, y el usuario ve un conflicto que no existe.
//   4. Y TAMPOCO puede solaparse con lo que escribe el caso POR OTRA PUERTA (bug 698):
//      adjuntar y quitar una evidencia guardan el caso igual —el trigger
//      `tg_caso_version_bloqueo` sube la version— y son la misma carrera con otro
//      nombre. `enSerie` es el turno compartido que las pone en fila.

import { useCallback, useEffect, useRef, useState } from 'react';
import * as api from '../api/casos';
import { ConflictoDeVersion } from '../api/cliente';
import {
  actualizarVersionDelEspejo,
  guardarEspejo,
  olvidarBorradorDeLaPestana,
  recordarEnLaPestana,
  olvidarEspejo,
  recordarEnCurso,
  limpiarEnCurso,
} from './borradorLocal';
import type { CasoDelFormulario, Faltante } from '../tipos';

// HU-36 no fija el intervalo. 2 s es el compromiso: por debajo se manda una peticion
// por pulsacion y por encima el usuario percibe que "no se esta guardando".
//
// AJ-EP (MG-ILE-918, tarea 918-4): el intervalo es el parametro `autoguardado_intervalo_segundos`,
// que el administrador cambia en «Reglas de negocio» sin redesplegar. Quien monta el hook lo pasa
// en `retardoMs`; estos 2 s quedan solo como respaldo mientras el parametro no llegue al cliente.
// ⚠ Hoy el registrador NO lo recibe: `/referencias/configuracion-del-formulario` no lo incluye y
// `/administracion/parametros` es solo del administrador (vacio de contrato anotado).
export const RETARDO_DE_AUTOGUARDADO_MS = 2000;

export const ESTADO_DE_GUARDADO = {
  LIMPIO: 'limpio',
  PENDIENTE: 'pendiente',
  GUARDANDO: 'guardando',
  GUARDADO: 'guardado',
  CONFLICTO: 'conflicto',
  ERROR: 'error',
};

/** Lo que el servidor contesta en cada guardado o apertura de borrador. */
type RespuestaDeBorrador = {
  caso_id: string;
  version_bloqueo: number;
  faltantes_para_enviar?: Faltante[];
  posibles_duplicados?: PosibleParecido[];
};

/** Un guardado en espera: sus secciones y el caso completo con el que se lanzo. */
/** Un caso que el servidor considera parecido al que se esta registrando (HU-07). */
export type PosibleParecido = {
  caso_id: string;
  numero?: string;
  producto?: string;
  notificante?: string;
  fecha_recepcion?: string;
};

type Pendiente = { secciones: Record<string, unknown>; casoCompleto?: CasoDelFormulario };

export function useBorrador({ alEnviar, retardoMs }: {
  alEnviar?: (respuesta: unknown) => void;
  /** El intervalo de autoguardado del parametro (918-4); sin el, `RETARDO_DE_AUTOGUARDADO_MS`. */
  retardoMs?: number | null;
} = {}) {
  // En un ref: cambiar el parametro no tiene que recrear `programarAutoguardado`.
  const retardoRef = useRef(RETARDO_DE_AUTOGUARDADO_MS);
  retardoRef.current = retardoMs && retardoMs > 0 ? retardoMs : RETARDO_DE_AUTOGUARDADO_MS;
  const [casoId, setCasoId] = useState<string | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [estado, setEstado] = useState(ESTADO_DE_GUARDADO.LIMPIO);
  const [faltantes, setFaltantes] = useState<Faltante[]>([]);
  // HU-07 · A QUIEN SE PARECE, MIENTRAS SE CAPTURA (CP-07-20, 4-sep-2026).
  //
  // El servidor ya lo mandaba en CADA guardado —`_resultado()` compone `faltantes` y
  // `posibles_duplicados` juntos, y su comentario dice literalmente que la advertencia
  // tiene que aparecer "al registrar", no al final—. Aqui se leia `faltantes_para_enviar`
  // y se tiraba el resto de la respuesta, asi que el aviso solo existia en el 409 del
  // envio. QA lo probo como pide la HU —"Dado que registro producto, lote, notificante y
  // fecha / Cuando coinciden"—, nunca llego al envio, y anoto "no advierte de duplicado".
  const [posiblesDuplicados, setPosiblesDuplicados] = useState<PosibleParecido[]>([]);
  const [error, setError] = useState<unknown>(null);

  // En refs y no en estado: se leen dentro de temporizadores y de callbacks que se
  // crearon con un render anterior. En estado, el autoguardado guardaria la version
  // que existia cuando se programo, no la vigente.
  const versionRef = useRef<number | null>(null);
  const casoIdRef = useRef<string | null>(null);
  // LA APERTURA EN VUELO (T-49). `abrir` fija `casoIdRef` cuando RESPONDE el `POST`, no
  // al lanzarlo, asi que entre la peticion y su respuesta el id vale `null` y cualquiera que
  // pregunte en esa rendija concluye que el caso no existe — y abre otro. Es la misma
  // carrera que T-47 cerro para la ADOPCION, en la otra puerta: alli el caso ya existia
  // y su id venia en camino; aqui el caso se esta creando. Se representa igual, con la
  // PROMESA en vuelo, para que el segundo en llegar espere en vez de abrir.
  const aperturaRef = useRef<Promise<RespuestaDeBorrador | null> | null>(null);
  const enVueloRef = useRef(false);
  // EL TURNO EN CURSO (bug 698). Es la promesa de "alguien esta escribiendo el caso
  // ahora mismo": el `PUT` en vuelo o la accion que `enSerie` esta ejecutando. No es la
  // promesa de la operacion —esa puede rechazar y quien espera no tiene nada que hacer
  // con su error—, sino un aplazado que se resuelve cuando el turno se suelta.
  const turnoRef = useRef<Promise<void> | null>(null);
  // `guardarAhora` se define mas abajo y quien suelta el turno lo necesita para vaciar
  // la cola de uno. El ref rompe el ciclo sin encadenar dos `useCallback`.
  const guardarAhoraRef = useRef<
    ((secciones: Record<string, unknown>, casoCompleto?: CasoDelFormulario) => void) | null
  >(null);
  const pendienteRef = useRef<Pendiente | null>(null);
  const temporizadorRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(temporizadorRef.current), []);

  /**
   * Toma el turno y devuelve como soltarlo. Lo comparten `guardarAhora` y `enSerie`
   * para que el turno sea UNO SOLO: dos cerrojos distintos para la misma fila del caso
   * no serializan nada, solo reparten la carrera en dos.
   */
  const tomarTurno = useCallback(() => {
    let soltar: () => void = () => {};
    const turno = new Promise<void>((resolver) => {
      soltar = resolver;
    });
    turnoRef.current = turno;
    enVueloRef.current = true;
    return () => {
      enVueloRef.current = false;
      // El pendiente se vacia AQUI y no lo consume quien suelta: asi la cola de uno se
      // dispara igual por los dos caminos, y con la version ya adoptada por el turno
      // que acaba de terminar.
      const encolado = pendienteRef.current;
      pendienteRef.current = null;
      if (encolado) guardarAhoraRef.current?.(encolado.secciones, encolado.casoCompleto);
      // Si el encolado arranco, YA instalo su propio turno (corre sincrono hasta su
      // primer `await`): solo se limpia el ref si el turno vigente sigue siendo este.
      if (turnoRef.current === turno) turnoRef.current = null;
      soltar();
    };
  }, []);

  const fijarVersion = useCallback((nueva: number) => {
    versionRef.current = nueva;
    setVersion(nueva);
  }, []);

  const aplicarRespuesta = useCallback(
    (respuesta: RespuestaDeBorrador) => {
      fijarVersion(respuesta.version_bloqueo);
      setFaltantes(respuesta.faltantes_para_enviar ?? []);
      setPosiblesDuplicados(respuesta.posibles_duplicados ?? []);
      return respuesta;
    },
    [fijarVersion],
  );

  /**
   * HU-01. Abre el caso en el servidor y deja el id disponible para los guardados.
   *
   * UN SOLO `POST` POR CASO, cueste lo que cueste (T-49). Sin este cerrojo, el
   * autoguardado de la pantalla (1,2 s, el `setTimeout` de `RegistroCaso` — no el de
   * este archivo), el boton "Guardar" y "Adjuntar" podian entrar los tres en la ventana
   * que abre el `POST`, ver `casoIdRef.current` todavia en `null` y abrir un caso cada uno. En dev quedaron cascarones vacios —notificante nulo,
   * `version_bloqueo` 1— creados con segundos de diferencia entre si.
   *
   * El cerrojo NO es un booleano "abriendo": eso obligaria a quien llega segundo a
   * reintentar o a rendirse, y en los dos casos pierde el id. Es la PROMESA de la
   * apertura en vuelo, asi que el segundo espera y recibe EXACTAMENTE el mismo caso que
   * el primero. Mismo patron que `adopcionRef` en `RegistroConectado` (T-47).
   */
  const abrir = useCallback(
    async (datos: Record<string, unknown>): Promise<RespuestaDeBorrador | null> => {
      // Ya hay una apertura en vuelo: se comparte, no se lanza otra.
      if (aperturaRef.current) return aperturaRef.current;
      // Y si el caso ya nacio, no hay nada que abrir. Se mira el REF y no el estado:
      // este callback puede estar corriendo con la clausura de un render anterior.
      if (casoIdRef.current) return null;

      setEstado(ESTADO_DE_GUARDADO.GUARDANDO);
      const apertura = api
        .abrirBorrador(datos as never)
        .then((cruda): RespuestaDeBorrador => {
          const respuesta = cruda as RespuestaDeBorrador;
          casoIdRef.current = respuesta.caso_id;
          setCasoId(respuesta.caso_id);
          recordarEnCurso(respuesta.caso_id);
          // Y en la PESTANA, para volver aqui si se recarga (CP-06-05).
          recordarEnLaPestana(respuesta.caso_id);
          aplicarRespuesta(respuesta);
          setEstado(ESTADO_DE_GUARDADO.GUARDADO);
          return respuesta;
        })
        .catch((error_) => { // `error` es el nombre convenido; aqui ya lo ocupa el estado
          setError(error_);
          setEstado(ESTADO_DE_GUARDADO.ERROR);
          throw error_;
        })
        .finally(() => {
          // Se suelta SIEMPRE, tambien tras un fallo: si la apertura no llego a crear
          // nada, el proximo intento tiene que poder abrir de verdad. Un cerrojo que no
          // se suelta deja el borrador sin poder guardarse jamas.
          //
          // La comparacion de identidad es por simetria con `adopcionRef`, que la
          // necesita de verdad —su efecto se rehace al cambiar `retomar` y puede haber
          // relevo—. Aqui hoy no puede haber una segunda apertura en vuelo (la guarda
          // de arriba la impide), pero cuesta cero y evita que alguien "arregle" el
          // hermano equivocado al ver que difieren.
          if (aperturaRef.current === apertura) aperturaRef.current = null;
        });

      aperturaRef.current = apertura;
      return apertura;
    },
    [aplicarRespuesta],
  );

  const guardarAhora = useCallback(
    async (secciones: Record<string, unknown>, casoCompleto?: CasoDelFormulario) => {
      const id = casoIdRef.current;
      if (!id) return null;

      // Si ya hay un PUT en vuelo, esto se acumula y sale cuando aquel termine —con
      // la version que aquel devuelva. Es lo que evita el 409 contra uno mismo.
      if (enVueloRef.current) {
        // EL PENDIENTE SE ACUMULA SIEMPRE, venga o no `casoCompleto`. Un intento de
        // condicionarlo a que llegara el caso entero rompio la prueba "el autoguardado
        // no se solapa consigo mismo": sin caso completo el guardado en espera se
        // perdia, y sus secciones no salian nunca. `casoCompleto` es opcional en el
        // tipo por eso, y quien lo consume ya comprueba si vino.
        pendienteRef.current = { secciones: { ...pendienteRef.current?.secciones, ...secciones }, casoCompleto };
        return null;
      }

      const soltarTurno = tomarTurno();
      setEstado(ESTADO_DE_GUARDADO.GUARDANDO);
      try {
        const version = versionRef.current;
        if (version === null) throw new Error('El caso no llegó a abrirse. Recarga la página e inténtalo de nuevo.');
        const respuesta = (await api.guardarBorrador(id, version, secciones)) as RespuestaDeBorrador;
        aplicarRespuesta(respuesta);
        if (casoCompleto) guardarEspejo(id, casoCompleto, respuesta.version_bloqueo);
        setEstado(ESTADO_DE_GUARDADO.GUARDADO);
        return respuesta;
      } catch (error_) { // idem: `error` ya es el estado del hook
        if (error_ instanceof ConflictoDeVersion) {
          // No se reintenta ni se pisa: HU-36 pide avisar. Reintentar con la version
          // del servidor seria exactamente "el ultimo en guardar gana en silencio",
          // que es lo que la historia existe para impedir.
          setEstado(ESTADO_DE_GUARDADO.CONFLICTO);
        } else {
          setEstado(ESTADO_DE_GUARDADO.ERROR);
        }
        setError(error_);
        throw error_;
      } finally {
        // Soltar el turno vacia la cola de uno: solo interesa el ultimo estado del
        // formulario, no la historia de pulsaciones intermedias.
        soltarTurno();
      }
    },
    [aplicarRespuesta, tomarTurno],
  );

  // El ref que `tomarTurno` consulta para vaciar la cola. Se refresca en cada render
  // porque `guardarAhora` se recrea si cambia `aplicarRespuesta`.
  guardarAhoraRef.current = (secciones, casoCompleto) => {
    guardarAhora(secciones, casoCompleto).catch(() => {});
  };

  /**
   * BUG 698 · HU-07 + HU-36 · ejecuta `accion` SIN que se cruce con un guardado.
   *
   * Adjuntar y quitar una evidencia escriben el caso igual que el autoguardado: el caso
   * de uso termina en `casos.guardar(caso)` y el trigger de la base sube
   * `version_bloqueo`. Con un `PUT` en vuelo, el que llega segundo encuentra la version
   * movida y recibe 409 `conflicto_de_concurrencia`. Cuando el que pierde es el adjunto,
   * el binario ya se subio al almacenamiento y la referencia no se registra: la evidencia
   * existe en el contenedor y no en el caso — "se pierde el adjunto" (QA, CP-REG-11).
   *
   * Se resuelve SERIALIZANDO, no reintentando: reintentar con la version del servidor es
   * "el ultimo en guardar gana en silencio", justo lo que HU-36 prohibe.
   *
   * Hace las dos mitades del turno:
   *   1. espera al `PUT` en vuelo Y a la cola de uno que sale en su `finally`;
   *   2. mientras `accion` corre, ocupa el turno, asi que un autoguardado que dispare
   *      se encola en vez de salir — y sale despues, con la version que `accion` dejo
   *      adoptada.
   *
   * Quien lo use debe ADOPTAR la version DENTRO de `accion`: si lo hiciera fuera, el
   * guardado encolado saldria con la version vieja y el 409 volveria por la otra puerta.
   */
  const enSerie = useCallback(
    async <T>(accion: () => Promise<T>): Promise<T> => {
      // `while` y no un solo `await`: al soltar un turno puede haber arrancado el
      // guardado encolado, que instala el suyo. Se espera a que no quede ninguno.
      while (turnoRef.current) await turnoRef.current;
      const soltarTurno = tomarTurno();
      try {
        return await accion();
      } finally {
        soltarTurno();
      }
    },
    [tomarTurno],
  );

  /** HU-36. Autoguardado: "guarda borrador, NO envia". */
  const programarAutoguardado = useCallback(
    (secciones: Record<string, unknown>, casoCompleto?: CasoDelFormulario) => {
      if (!casoIdRef.current) return;
      setEstado(ESTADO_DE_GUARDADO.PENDIENTE);
      clearTimeout(temporizadorRef.current);
      temporizadorRef.current = setTimeout(() => {
        guardarAhora(secciones, casoCompleto).catch(() => {});
      }, retardoRef.current);
    },
    [guardarAhora],
  );

  /** HU-08. Con `aceptandoDuplicado` se reintenta tras el aviso de HU-07. */
  const enviar = useCallback(
    async ({ aceptandoDuplicado = false } = {}) => {
      const id = casoIdRef.current;
      if (!id) return null;
      // Un autoguardado en vuelo cambiaria la version bajo los pies del envio.
      clearTimeout(temporizadorRef.current);
      const respuesta = await api.enviarCaso(id, {
        duplicadoAdvertidoAceptado: aceptandoDuplicado,
      });
      olvidarEspejo(id);
      limpiarEnCurso();
      olvidarBorradorDeLaPestana();
      alEnviar?.(respuesta);
      return respuesta;
    },
    [alEnviar],
  );

  return {
    casoId,
    /**
     * El id VIGENTE, leido del ref (T-49).
     *
     * `casoId` es estado de React y llega tarde: un callback que ya esta corriendo
     * —o el temporizador del autoguardado, que retiene la clausura con la que se
     * programo— sigue viendo `null` despues de que el caso existe. Decidir "¿hay que
     * abrir uno?" con ese `null` es la otra mitad de los duplicados. El estado se
     * queda para PINTAR; esta funcion es para DECIDIR.
     */
    idActual: useCallback(() => casoIdRef.current, []),
    version,
    estado,
    faltantes,
    posiblesDuplicados,
    error,
    abrir,
    guardarAhora,
    programarAutoguardado,
    enviar,
    // BUG 698: para lo que escribe el caso por otra puerta (adjuntar, quitar).
    enSerie,
    // Para rehidratarse tras una recarga, y para RECOGER una version que movio el
    // servidor por un camino que no pasa por aqui (asociar o quitar una evidencia,
    // HU-07: el trigger de la base incrementa `version_bloqueo` en ese UPDATE).
    //
    // Corrige tambien el espejo: si solo se arreglara la copia en memoria, recargar
    // volveria a leer la version vieja del almacenamiento y el conflicto falso
    // reaparecería intacto.
    adoptar: useCallback(
      (id: string, versionGuardada: number) => {
        casoIdRef.current = id;
        setCasoId(id);
        fijarVersion(versionGuardada);
        actualizarVersionDelEspejo(id, versionGuardada);
      },
      [fijarVersion],
    ),
  };
}
